"use client";

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives/Popover";
import type { ReportCatalog } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { useLocationTree } from "../hooks/useLocationTree";
import { keepOffices, singleOffice } from "../lib/locationTree";
import { LocationTree } from "./LocationTree";

interface FloorPickerProps {
  catalog: ReportCatalog;
  /** The floor picked today, if any. */
  floorId: string | null;
  /** Offices the pick is limited to; none means every office. */
  officeIds: string[];
  onPick: (floorId: string) => void;
  className?: string;
}

/**
 * A select-like button that picks one floor from the division → office →
 * floor tree, narrowed to the offices given: one office offers its floors
 * alone. The button reads "Office · Floor" once a floor is picked.
 */
export const FloorPicker: React.FC<FloorPickerProps> = ({
  catalog,
  floorId,
  officeIds,
  onPick,
  className
}) => {
  const floors =
    catalog.dimensions.find((d) => d.key === "floor")?.members ?? [];
  const offices =
    catalog.dimensions.find((d) => d.key === "office")?.members ?? [];
  const floorTree = useLocationTree(catalog.dimensions, "floor");
  const [isOpen, setIsOpen] = useState(false);

  const scopedTree =
    officeIds.length > 0 ? keepOffices(floorTree, officeIds) : floorTree;
  const onlyOffice = singleOffice(scopedTree);
  const nodes = onlyOffice ? onlyOffice.children : scopedTree;
  const floor = floors.find((f) => f.id === floorId);
  const floorOffice =
    onlyOffice?.label ?? offices.find((o) => o.id === floor?.parent)?.label;
  const label = floor
    ? floorOffice
      ? `${floorOffice} · ${floor.label}`
      : floor.label
    : "Pick a floor";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          className={cn(
            "flex h-8 w-[260px] items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-3 text-[13px] hover:bg-neutral-50",
            className
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-modal w-80 p-0">
        <LocationTree
          nodes={nodes}
          selected={floorId ? [floorId] : []}
          mode="single"
          searchPlaceholder="Search floors…"
          emptyText="No floor plans in these offices."
          onChange={([id]) => {
            if (!id) {
              return;
            }
            onPick(id);
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
